import React, { useEffect, useState } from 'react';
import { Checkbox, Radio, RadioGroup, FormControlLabel, FormGroup, Typography, TextField } from '@mui/material';
import { AQUA } from '@wave-engagement/wave-react';
import Button from './Button';
import Slider from './Slider';

const LESSON_TYPES = {
  CHALLENGE: 'CHALLENGE',
  REFLECTION: 'REFLECTION',
  NONE: 'NONE'
};

const EMPTY_MAP = () => JSON.parse(JSON.stringify({
  [LESSON_TYPES.CHALLENGE]: [],
  [LESSON_TYPES.REFLECTION]: [],
  [LESSON_TYPES.NONE]: []
}));

const EngagementQuestionForm = ({ engagementQuestions = [], onSubmit, style = {}, isLoading = false, frontContent }) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [responses, setResponses] = useState(EMPTY_MAP());
  const [questions, setQuestions] = useState(EMPTY_MAP());

  // Initialize the responses
  useEffect(() => {
    const questionsCopy = EMPTY_MAP();
    const responsesCopy = EMPTY_MAP();
    engagementQuestions.forEach(eq => {
      const lessonType = eq.getLessonType() ?? LESSON_TYPES.NONE;
      questionsCopy[lessonType].push(eq);
      responsesCopy[lessonType].push({
        engagementQuestionId: eq.getId(),
        value: null
      });
    });
    setQuestions(questionsCopy);
    setResponses(responsesCopy);
  }, [JSON.stringify(engagementQuestions.map(eq => eq.getId()))]);

  const setValueForQuestion = (type, index, value) => {
    if (isLoading) return;
    const responsesCopy = [...responses[type]];
    responsesCopy[index] = { ...responsesCopy[index], value };
    setResponses({ ...responses, [type]: responsesCopy });
  }

  const onSubmitInitial = () => {
    const questionsCopy = [...questions[LESSON_TYPES.CHALLENGE], ...questions[LESSON_TYPES.REFLECTION], ...questions[LESSON_TYPES.NONE]];
    const responsesCopy = ([...responses[LESSON_TYPES.CHALLENGE], ...responses[LESSON_TYPES.REFLECTION], ...responses[LESSON_TYPES.NONE]]).map((response, index) => {
      let value = response.value;
      const type = questionsCopy[index].getType();
      switch (type) {
        case 'SELECT_ALL':
          value = value?.join(';;;');
          break;
        case 'SLIDER':
          value = value?.toString();
          break;
      }
      return ({
        engagementQuestionId: response.engagementQuestionId,
        value,
        type
      });
    }).filter(response => response.value != null);
    onSubmit(responsesCopy);
  }

  // Return true if any values are not null
  const hasInputChanged = () => {
    // Check if any of the responses have changed
    for (const type in responses) {
      for (const response of responses[type]) {
        if (response.value != null) return true;
      }
    }

    return false;
  }

  const renderQuestion = (type, question, index) => {
    const value = responses[type][index]?.value;

    return (
      <div key={index} style={{ marginTop: '20px', ...style }}>
        {/* Question and Points */}
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <Typography variant='h5' style={{ flex: 1 }}>{question.getDescription()}</Typography>
          <Typography variant='h5' style={{ width: '100px', textAlign: 'right' }}>+{question.getPoints()} pts</Typography>
        </div>

        {/* Yes/No Checkbox */}
        {question.getType() === 'CHECKBOX' && (
          <RadioGroup value={value} onChange={e => setValueForQuestion(type, index, e.target.value)} row>
            <FormControlLabel value={'true'} control={<Radio />} label={'Yes'} disabled={isLoading} />
            <FormControlLabel value={'false'} control={<Radio />} label={'No'} disabled={isLoading} />
          </RadioGroup>
        )}

        {/* Select All */}
        {question.getType() === 'SELECT_ALL' && (
          <FormGroup>
            {question.getOptions().sort((a, b) => a.getOrder() - b.getOrder()).map((option, i) => (
              <FormControlLabel key={i} label={option.getDescription()} disabled={isLoading} control={
                <Checkbox
                  checked={value?.includes(option.getDescription())}
                  onChange={e => {
                    let newVal = value ?? [];
                    if (e.target.checked) newVal.push(option.getDescription());
                    else newVal = newVal.filter(v => v !== option.getDescription());
                    setValueForQuestion(type, index, newVal);
                  }}
                />
              } />
            ))}
          </FormGroup>
        )}

        {/* Multiple Choice */}
        {question.getType() === 'MULTIPLE_CHOICE' && (
          <RadioGroup value={value} onChange={e => setValueForQuestion(type, index, e.target.value)}>
            {question.getOptions().sort((a, b) => a.getOrder() - b.getOrder()).map((option, index) => (
              <FormControlLabel key={index} disabled={isLoading} value={option.getDescription()} control={<Radio />} label={option.getDescription()} />
            ))}
          </RadioGroup>
        )}

        {/* Photo Upload */}
        {question.getType() === 'PHOTO_UPLOAD' && (
          <input
            type='file'
            accept='image/jpeg, image/png'
            disabled={isLoading}
            onChange={e => {
              const file = e.target.files[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = e => setValueForQuestion(type, index, e.target.result);
                reader.readAsDataURL(file);
              }
            }}
          />
        )}

        {/* Slider */}
        {question.getType() === 'SLIDER' && (
          <Slider
            value={value}
            onChange={newValue => setValueForQuestion(type, index, newValue)}
            disabled={isLoading}
            value1={question.getSliderValue1()}
            value5={question.getSliderValue5()}
          />
        )}

        {/* Open Text */}
        {question.getType() === 'OPEN_TEXT' && (
          <TextField
            value={value}
            onChange={e => setValueForQuestion(type, index, e.target.value?.trim()?.length === 0 ? null : e.target.value )}
            disabled={isLoading}
            multiline
            rows={4}
            variant="filled"
            fullWidth
            style={{ marginTop: '10px'}}
            inputProps={{ maxLength: 16_000 }}
          />
        )}
      </div>
    );
  }

  const pageContent = [];
  const basicQuestions = questions[LESSON_TYPES.NONE].map((question, index) => renderQuestion(LESSON_TYPES.NONE, question, index));
  const challengeQuestions = questions[LESSON_TYPES.CHALLENGE].map((question, index) => renderQuestion(LESSON_TYPES.CHALLENGE, question, index));
  const reflectionQuestions = questions[LESSON_TYPES.REFLECTION].map((question, index) => renderQuestion(LESSON_TYPES.REFLECTION, question, index));
  if (basicQuestions.length > 0) pageContent.push(basicQuestions);
  if (challengeQuestions.length > 0) pageContent.push(
    <>
      <div style={{ width: '100%', backgroundColor: AQUA, padding: 10 }}>
        <Typography variant='h2' style={{ marginBottom: '1em' }}>Challenge</Typography>
      </div>
      <div style={{ paddingTop: 10, paddingLeft: 10, paddingRight: 10, paddingBottom: 20 }}>
        {challengeQuestions}
      </div>
    </>
  );
  if (reflectionQuestions.length > 0) pageContent.push(
    <>
      <div style={{ width: '100%', backgroundColor: AQUA, padding: 10 }}>
        <Typography variant='h2' style={{ marginBottom: '1em' }}>Reflection</Typography>
      </div>
      <div style={{ paddingTop: 10, paddingLeft: 10, paddingRight: 10, paddingBottom: 20 }}>
        {reflectionQuestions}
      </div>
    </>
  );

  return (
    <div>
      {/* Front Content */}
      {pageIndex === 0 && frontContent}

      {/* Questions */}
      <div style={{ border: `solid ${AQUA}`, borderColor: AQUA, borderWidth: 7, borderRadius: 10, padding: '5px' }}>
        {pageContent[pageIndex]}
      </div>

      {/* Buttons */}
      {pageIndex === pageContent.length - 1 &&
      <Button fullWidth onClick={onSubmitInitial} style={{ marginTop: 50 }} isLoading={isLoading} disabled={!hasInputChanged()}>Submit</Button>}
      {pageIndex < pageContent.length - 1 &&
      <Button fullWidth onClick={() => setPageIndex(pageIndex + 1)} style={{ marginTop: 50 }}>Next</Button>}
      {pageIndex > 0 && pageContent.length > 1 &&
      <Button fullWidth secondary onClick={() => setPageIndex(pageIndex - 1)} style={{ marginTop: 10 }}>Back</Button>}
    </div>
  );
}
export default EngagementQuestionForm;